import { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";

/**
 * Patch materials in `projectMaterial.materials` in place, matched by id.
 *
 * The mapping UI renders `useProject().activeMaterial`, which hands each SpeckleGroup a fresh
 * `{ ...material }` copy on every recompute, and SpeckleGroup keeps a deep clone of that prop.
 * Mapping a component therefore only ever touched the clone: the next recompute of
 * `activeMaterial` — which is what filtering, searching and sorting all force — re-cloned from
 * the untouched store data and the mapping disappeared until the page was reloaded (RTLCA-5231).
 * Feeding the update back here keeps the source of truth in step with what the user just did.
 *
 * Matching is by id rather than index: `activeMaterial` merges, filters and sorts the per-model
 * buckets, so positions in the rendered list say nothing about positions in the store.
 *
 * Three rules keep a payload from damaging the tree, all learned from breaking it:
 *
 * 1. A payload that carries child rows describes a group and is matched as a group, even when its
 *    id is also a child's. The API hands a group and its first child the same id on every single
 *    group — `MaterialAggregator.Aggregate` builds the parent from `bm.First()` and the first
 *    child from that same row — so without this the group-level include-mapping toggle landed on
 *    that first child, overwrote its identifier with the group's and left the rest of the group
 *    unpatched (RTLCA-5474).
 * 2. Otherwise children are matched before groups, and a payload found as a child never touches a
 *    group. An id can sit in both roles (a material can be a group of its own and a child of
 *    another grouping), and applying a leaf payload to a group renamed that group and emptied it.
 * 3. `children` is only taken from a payload that actually carries rows. A leaf reports
 *    `children: null`, which would otherwise erase the children the store already had.
 *
 * Rules 1 and 2 read the same field from opposite sides, which is what makes them consistent: a
 * payload either carries rows and is a group, or reports none and is a leaf wherever it is found.
 *
 * Every occurrence is patched, not just the first: the same material can appear under more than
 * one group, and all of them should show the mapping the user just made.
 *
 * Returns the number of materials patched, which callers can use to detect a payload that
 * matched nothing.
 */
export const mergeMaterialUpdates = (
  materialsByModel: { [modelName: string]: SpeckleMaterial[] } | undefined | null,
  updates: Array<SpeckleMaterial | null | undefined>
): number => {
  if (!materialsByModel) return 0;

  const buckets = Object.values(materialsByModel).filter((materials): materials is SpeckleMaterial[] => Array.isArray(materials));

  let patched = 0;

  for (const update of updates) {
    if (!update?.id) continue;

    const children: SpeckleMaterial[] = [];
    const groups: SpeckleMaterial[] = [];

    for (const materials of buckets) {
      for (const material of materials) {
        if (material.id === update.id) groups.push(material);
        for (const child of material.children ?? []) {
          if (child.id === update.id) children.push(child);
        }
      }
    }

    const targets = isGroupShapedUpdate(update) ? groups : children.length > 0 ? children : groups;

    for (const target of targets) {
      applyFields(target, update);
      patched++;
    }
  }

  return patched;
};

// The payload's own shape decides which role it describes, because an id cannot: every group
// shares one with its first child (see the module doc, rule 1).
const isGroupShapedUpdate = (update: SpeckleMaterial): boolean => Array.isArray(update.children) && update.children.length > 0;

const applyFields = (target: SpeckleMaterial, update: SpeckleMaterial) => {
  const { children, ...fields } = update;
  // Assign rather than replace: the payload carries only the fields the API touched, so keys it
  // omits keep the values already in the store.
  Object.assign(target, fields);
  if (Array.isArray(children) && children.length > 0) target.children = children;
};

// Same group/child disambiguation as mergeMaterialUpdates (no leaf-to-group fallback here, since a
// caller with only one known group has nowhere safe to fall back to). Returns whether it patched anything.
export const applyMaterialUpdateToGroup = (group: SpeckleMaterial, update: SpeckleMaterial | null | undefined): boolean => {
  if (!update?.id) return false;

  if (isGroupShapedUpdate(update)) {
    if (update.id !== group.id) return false;
    applyFields(group, update);
    return true;
  }

  const child = group.children?.find((candidate) => candidate.id === update.id);
  if (!child) return false;
  applyFields(child, update);
  return true;
};
